import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { tenantsApi } from "../../../services/api";
import { integrationsApi } from "../../../services/api/integrations";
import { useIntegrations } from "../../../contexts/IntegrationContext";
import Icon from "../../../components/AppIcon";

const CompanySettings = () => {
  const { user, isTenantAdmin } = useAuth();
  const { refreshIntegrations } = useIntegrations();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Integration state
  const [integrationsOverview, setIntegrationsOverview] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null); // { type: 'hr' | 'till', provider: {...} }
  const [integrationForm, setIntegrationForm] = useState({
    apiKey: "",
    baseUrl: "",
    username: "",
    password: "",
    additionalConfiguration: "",
  });

  const [formData, setFormData] = useState({
    name: "",
    contactEmail: "",
    contactPhone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    county: "",
    postCode: "",
    companyRegistrationNumber: "",
    vatNumber: "",
    logoUrl: "",
    primaryColor: "",
  });

  const fetchTenant = async () => {
    if (!user?.tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await tenantsApi.getCurrent();
      const data = response.data ?? response.Data;
      setTenant(data);
      if (data) {
        setFormData({
          name: data.name || "",
          contactEmail: data.contactEmail || "",
          contactPhone: data.contactPhone || "",
          addressLine1: data.addressLine1 || "",
          addressLine2: data.addressLine2 || "",
          city: data.city || "",
          county: data.county || "",
          postCode: data.postCode || "",
          companyRegistrationNumber: data.companyRegistrationNumber || "",
          vatNumber: data.vatNumber || "",
          logoUrl: data.logoUrl || "",
          primaryColor: data.primaryColor || "",
        });
      }
    } catch (err) {
      setError(err.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrations = async () => {
    if (!user?.tenantId) return;

    setIntegrationsLoading(true);
    try {
      const response = await integrationsApi.getSettingsOverview();
      if (response.success) {
        setIntegrationsOverview(response.data);
      }
    } catch (err) {
      console.error("Failed to load integrations:", err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  useEffect(() => {
    fetchTenant();
    fetchIntegrations();
  }, [user?.tenantId]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!tenant) return;

    setIsSaving(true);
    try {
      await tenantsApi.update(tenant.id, {
        name: formData.name,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone || null,
        addressLine1: formData.addressLine1 || null,
        addressLine2: formData.addressLine2 || null,
        city: formData.city || null,
        county: formData.county || null,
        postCode: formData.postCode || null,
        logoUrl: formData.logoUrl || null,
        primaryColor: formData.primaryColor || null,
      });

      setIsEditing(false);
      fetchTenant();
      showNotification("Company details updated successfully");
    } catch (err) {
      showNotification(
        err.message || "Failed to update company details",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigureIntegration = (type, provider) => {
    // Find existing settings if any
    const existingSetting =
      type === "hr"
        ? integrationsOverview?.hrProviders?.find(
            (p) => p.providerId === provider.providerId && p.isConfigured,
          )
        : integrationsOverview?.tillProviders?.find(
            (p) => p.providerId === provider.providerId && p.isConfigured,
          );

    setIntegrationForm({
      apiKey: "",
      baseUrl: "",
      username: "",
      password: "",
      additionalConfiguration: "",
    });
    setEditingIntegration({ type, provider });
  };

  const handleSaveIntegration = async (e) => {
    e.preventDefault();
    if (!editingIntegration) return;

    setIsSaving(true);
    try {
      await integrationsApi.saveSettings({
        integrationProviderId: editingIntegration.provider.providerId,
        isEnabled: true,
        apiKey: integrationForm.apiKey || null,
        baseUrl: integrationForm.baseUrl || null,
        username: integrationForm.username || null,
        password: integrationForm.password || null,
        additionalConfiguration:
          integrationForm.additionalConfiguration || null,
      });

      setEditingIntegration(null);
      fetchIntegrations();
      refreshIntegrations();
      showNotification(
        `${editingIntegration.provider.providerName} integration configured successfully`,
      );
    } catch (err) {
      showNotification(
        err.message || "Failed to configure integration",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisableIntegration = async (settingsId, providerName) => {
    if (
      !confirm(
        `Are you sure you want to disable the ${providerName} integration?`,
      )
    )
      return;

    try {
      await integrationsApi.deleteSettings(settingsId);
      fetchIntegrations();
      refreshIntegrations();
      showNotification(`${providerName} integration disabled`);
    } catch (err) {
      showNotification(err.message || "Failed to disable integration", "error");
    }
  };

  const getActiveProvider = (providers) => {
    return providers?.find((p) => p.isEnabled && p.isConfigured);
  };

  if (!user?.tenantId) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <Icon
            name="Building"
            size={48}
            className="mx-auto mb-4 text-muted-foreground"
          />
          <h2 className="text-xl font-semibold mb-2">No Company Associated</h2>
          <p className="text-muted-foreground">
            Your account is not associated with any company/tenant.
          </p>
        </div>
      </div>
    );
  }

  const activeHrProvider = getActiveProvider(integrationsOverview?.hrProviders);
  const activeTillProvider = getActiveProvider(
    integrationsOverview?.tillProviders,
  );

  return (
    <>
      <Helmet>
        <title>Company Settings - Rocket Tronc</title>
      </Helmet>

      <div className="p-6 max-w-4xl mx-auto">
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-bold">Company Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization's details, branding, and integrations
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading company details...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-outline btn-sm ml-auto"
              onClick={fetchTenant}
            >
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && !error && tenant && (
          <div className="grid gap-6">
            {/* Company Info */}
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h2 className="text-lg font-semibold">Company Information</h2>
                {isTenantAdmin && !isEditing && (
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Icon name="Pencil" size={16} />
                    Edit
                  </button>
                )}
              </div>
              <div className="card-content">
                {isEditing ? (
                  <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Company Name</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Contact Email</label>
                        <input
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contactEmail: e.target.value,
                            })
                          }
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Contact Phone</label>
                        <input
                          type="tel"
                          value={formData.contactPhone}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              contactPhone: e.target.value,
                            })
                          }
                          className="input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label">Address Line 1</label>
                      <input
                        type="text"
                        value={formData.addressLine1}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine1: e.target.value,
                          })
                        }
                        className="input"
                      />
                    </div>

                    <div>
                      <label className="label">Address Line 2</label>
                      <input
                        type="text"
                        value={formData.addressLine2}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine2: e.target.value,
                          })
                        }
                        className="input"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="label">City</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) =>
                            setFormData({ ...formData, city: e.target.value })
                          }
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">County</label>
                        <input
                          type="text"
                          value={formData.county}
                          onChange={(e) =>
                            setFormData({ ...formData, county: e.target.value })
                          }
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="label">Post Code</label>
                        <input
                          type="text"
                          value={formData.postCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              postCode: e.target.value,
                            })
                          }
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSaving}
                      >
                        {isSaving ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        Company Name
                      </dt>
                      <dd className="font-medium">{tenant.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        Contact Email
                      </dt>
                      <dd className="font-medium">{tenant.contactEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">
                        Contact Phone
                      </dt>
                      <dd className="font-medium">
                        {tenant.contactPhone || "-"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground">Address</dt>
                      <dd className="font-medium">
                        {[
                          tenant.addressLine1,
                          tenant.addressLine2,
                          tenant.city,
                          tenant.postCode,
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>

            {/* Integrations Section */}
            {isTenantAdmin && (
              <div className="card">
                <div className="card-header">
                  <h2 className="text-lg font-semibold">System Integrations</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Connect your HR and Till systems to automatically sync
                    employee and sales data
                  </p>
                </div>
                <div className="card-content space-y-6">
                  {integrationsLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner mx-auto" />
                    </div>
                  ) : (
                    <>
                      {/* HR Integration */}
                      <div className="border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon
                                name="Users"
                                size={20}
                                className="text-primary"
                              />
                            </div>
                            <div>
                              <h3 className="font-semibold">HR System</h3>
                              <p className="text-sm text-muted-foreground">
                                Sync employees, job roles, and clock records
                              </p>
                            </div>
                          </div>
                          {activeHrProvider && (
                            <span className="badge badge-success">
                              Connected
                            </span>
                          )}
                        </div>

                        {activeHrProvider ? (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">
                                  {activeHrProvider.providerName}
                                </p>
                                {activeHrProvider.lastSyncedOn && (
                                  <p className="text-xs text-muted-foreground">
                                    Last synced:{" "}
                                    {new Date(
                                      activeHrProvider.lastSyncedOn,
                                    ).toLocaleString()}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  className="btn btn-sm btn-outline"
                                  onClick={() =>
                                    handleConfigureIntegration(
                                      "hr",
                                      activeHrProvider,
                                    )
                                  }
                                >
                                  Configure
                                </button>
                                <button
                                  className="btn btn-sm btn-ghost text-error"
                                  onClick={() =>
                                    handleDisableIntegration(
                                      activeHrProvider.settingsId,
                                      activeHrProvider.providerName,
                                    )
                                  }
                                >
                                  Disconnect
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {integrationsOverview?.hrProviders
                              ?.filter((p) => p.isProviderActive)
                              .map((provider) => (
                                <button
                                  key={provider.providerId}
                                  className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
                                  onClick={() =>
                                    handleConfigureIntegration("hr", provider)
                                  }
                                >
                                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                    <Icon name="Link" size={16} />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {provider.providerName}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {provider.providerCode}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            {(!integrationsOverview?.hrProviders ||
                              integrationsOverview.hrProviders.length ===
                                0) && (
                              <p className="text-sm text-muted-foreground col-span-2">
                                No HR providers available. Contact your
                                administrator.
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Till Integration */}
                      <div className="border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                              <Icon
                                name="PoundSterling"
                                size={20}
                                className="text-secondary"
                              />
                            </div>
                            <div>
                              <h3 className="font-semibold">
                                Till / POS System
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Sync sales, tips, and TRONC data
                              </p>
                            </div>
                          </div>
                          {activeTillProvider && (
                            <span className="badge badge-success">
                              Connected
                            </span>
                          )}
                        </div>

                        {activeTillProvider ? (
                          <div className="bg-muted/50 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">
                                  {activeTillProvider.providerName}
                                </p>
                                {activeTillProvider.lastSyncedOn && (
                                  <p className="text-xs text-muted-foreground">
                                    Last synced:{" "}
                                    {new Date(
                                      activeTillProvider.lastSyncedOn,
                                    ).toLocaleString()}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  className="btn btn-sm btn-outline"
                                  onClick={() =>
                                    handleConfigureIntegration(
                                      "till",
                                      activeTillProvider,
                                    )
                                  }
                                >
                                  Configure
                                </button>
                                <button
                                  className="btn btn-sm btn-ghost text-error"
                                  onClick={() =>
                                    handleDisableIntegration(
                                      activeTillProvider.settingsId,
                                      activeTillProvider.providerName,
                                    )
                                  }
                                >
                                  Disconnect
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {integrationsOverview?.tillProviders
                              ?.filter((p) => p.isProviderActive)
                              .map((provider) => (
                                <button
                                  key={provider.providerId}
                                  className="flex items-center gap-3 p-3 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left"
                                  onClick={() =>
                                    handleConfigureIntegration("till", provider)
                                  }
                                >
                                  <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                    <Icon name="Link" size={16} />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {provider.providerName}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {provider.providerCode}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            {(!integrationsOverview?.tillProviders ||
                              integrationsOverview.tillProviders.length ===
                                0) && (
                              <p className="text-sm text-muted-foreground col-span-2">
                                No Till providers available. Contact your
                                administrator.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Subscription Info (Read-only) */}
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold">Subscription</h2>
              </div>
              <div className="card-content">
                <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Plan</dt>
                    <dd className="font-medium">
                      <span className="badge badge-secondary">
                        {tenant.subscriptionPlan}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Max Users</dt>
                    <dd className="font-medium">{tenant.maxUsers}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">
                      Max Employees
                    </dt>
                    <dd className="font-medium">{tenant.maxEmployees}</dd>
                  </div>
                </dl>
                <p className="text-sm text-muted-foreground mt-4">
                  Contact support to change your subscription plan.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Integration Configuration Modal */}
        {editingIntegration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">
                    Configure {editingIntegration.provider.providerName}
                  </h2>
                  <button
                    onClick={() => setEditingIntegration(null)}
                    className="p-2 hover:bg-muted rounded-lg"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your {editingIntegration.provider.providerName} API
                  credentials
                </p>
              </div>

              <form onSubmit={handleSaveIntegration} className="p-6 space-y-4">
                {/* Rocket internal integrations - no credentials needed */}
                {(editingIntegration.provider.providerCode === "RocketHR" ||
                  editingIntegration.provider.providerCode === "RocketPOS") ? (
                 <>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 text-success">
                    <Icon name="CheckCircle" size={20} />
                    <span className="font-medium">No configuration required</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Rocket integrations are automatically configured for your tenant.
                    Just click Connect to enable data syncing.
                  </p>
                </div>
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setEditingIntegration(null)}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="btn btn-primary"
                  >
                    {isSaving ? "Connecting..." : "Connect"}
                  </button>
                </div>
                </>
                ) : (
                 <>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    API Key
                  </label>
                  <input
                    type="password"
                    value={integrationForm.apiKey}
                    onChange={(e) =>
                      setIntegrationForm({
                        ...integrationForm,
                        apiKey: e.target.value,
                      })
                    }
                    className="input w-full"
                    placeholder="Enter your API key"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={integrationForm.baseUrl}
                    onChange={(e) =>
                      setIntegrationForm({
                        ...integrationForm,
                        baseUrl: e.target.value,
                      })
                    }
                    className="input w-full"
                    placeholder="https://api.example.com"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <button
                    type="button"
                    onClick={() => setEditingIntegration(null)}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="btn btn-primary"
                  >
                    {isSaving ? "Saving..." : "Connect"}
                  </button>
                </div>
                </>
                )}
               </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CompanySettings;
